/**
 * The adaptive player, in a browser.
 *
 * This is deliberately the same design as `adaptive_music_player.gd` in the
 * game: every layer plays all of the time and only its volume changes. They are
 * never started or stopped independently, so there is nothing to drift, and a
 * layer arriving is a fade rather than a switch.
 *
 * Matching the game is the point. If the editor mixed differently, it would be
 * a preview of something nobody ships.
 *
 * One difference, and it is in the browser's favour: Web Audio schedules every
 * source against a single clock, so the drift correction the Godot player needs
 * has nothing to do here.
 */

import { SR } from "../src/engine.js";

const FADE_TIME = 2.5;

/**
 * How far above its threshold a layer takes to become fully audible.
 *
 * A layer fades in over a window rather than switching on, so a colony sitting
 * either side of a boundary does not make the music stutter between two states.
 */
const WINDOW = 0.15;

export interface LoadedLayer {
  role: string;
  enterAt: number;
  samples: Float64Array;
}

export class AdaptivePlayer {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private sources = new Map<string, AudioBufferSourceNode>();
  private gains = new Map<string, GainNode>();
  private enterAt = new Map<string, number>();
  private intensity = 1;
  private startedAt = 0;
  private loop = true;
  private duration = 0;

  get playing(): boolean {
    return this.sources.size > 0;
  }

  /**
   * Seconds since playback began, wrapped to the loop length.
   *
   * Output latency is subtracted, because `currentTime` is where the audio
   * engine has got to, not what has reached the speakers. On Windows that gap
   * is tens of milliseconds and is enough to see a playhead running ahead of
   * what you can hear.
   */
  position(): number {
    if (!this.ctx || !this.playing || this.duration <= 0) return 0;
    const latency = this.ctx.outputLatency || this.ctx.baseLatency || 0;
    const elapsed = this.ctx.currentTime - latency - this.startedAt;
    if (elapsed <= 0) return 0;
    return this.loop ? elapsed % this.duration : Math.min(elapsed, this.duration);
  }

  /**
   * Take a freshly rendered pack.
   *
   * Float64 is converted to Float32 here because that is what Web Audio takes.
   * The exported WAVs are written from the Float64 originals, so nothing that
   * ships passes through this conversion.
   */
  load(layers: LoadedLayer[], loop: boolean): void {
    this.stop();
    const ctx = this.audio();
    this.buffers.clear();
    this.enterAt.clear();
    this.loop = loop;
    this.duration = 0;

    for (const layer of layers) {
      // Created at the rate the audio was RENDERED at, not the rate the output
      // happens to run at. Passing ctx.sampleRate tells Web Audio these samples
      // are 48kHz when they are 44.1kHz, so the track plays about 9% fast and a
      // little sharp, and the playhead falls behind because it is working from
      // the real length. The browser resamples correctly when told the truth.
      const buffer = ctx.createBuffer(1, layer.samples.length, SR);
      const channel = buffer.getChannelData(0);
      for (let i = 0; i < layer.samples.length; i++) channel[i] = layer.samples[i];
      this.buffers.set(layer.role, buffer);
      this.enterAt.set(layer.role, layer.enterAt);
      this.duration = Math.max(this.duration, buffer.duration);
    }
  }

  async play(): Promise<void> {
    if (this.buffers.size === 0) return;
    const ctx = this.audio();
    // Browsers start the context suspended until a user gesture. Resuming
    // without checking would leave the page looking like it is playing silence.
    if (ctx.state === "suspended") await ctx.resume();

    this.stopSources();

    // Every source is scheduled for the same instant. Starting them one at a
    // time is the single thing that would put the pack permanently out of
    // phase, and it would sound like a mistake in the music rather than a bug.
    const at = ctx.currentTime + 0.05;
    for (const [role, buffer] of this.buffers) {
      const gain = ctx.createGain();
      gain.gain.value = this.levelFor(role);
      gain.connect(ctx.destination);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = this.loop;
      source.connect(gain);
      source.start(at);

      this.sources.set(role, source);
      this.gains.set(role, gain);
    }
    this.startedAt = at;
  }

  stop(): void {
    this.stopSources();
  }

  /** 0 is a quiet colony, 1 is everything wrong at once. */
  setIntensity(value: number): void {
    this.intensity = Math.max(0, Math.min(1, value));
    if (!this.ctx) return;
    for (const [role, gain] of this.gains) {
      gain.gain.cancelScheduledValues(this.ctx.currentTime);
      gain.gain.setValueAtTime(gain.gain.value, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(
        this.levelFor(role),
        this.ctx.currentTime + FADE_TIME,
      );
    }
  }

  /** What each layer is currently contributing, for the meters. */
  levels(): { role: string; level: number }[] {
    return [...this.buffers.keys()].map((role) => ({ role, level: this.levelFor(role) }));
  }

  /** The pressure a layer arrives at. The stack draws itself from these. */
  enterAtFor(role: string): number {
    return this.enterAt.get(role) ?? 0;
  }

  private levelFor(role: string): number {
    const enter = this.enterAt.get(role) ?? 0;
    if (enter <= 0) return 1;
    return Math.max(0, Math.min(1, (this.intensity - enter) / WINDOW));
  }

  private stopSources(): void {
    for (const source of this.sources.values()) {
      try {
        source.stop();
      } catch {
        // Already stopped. Nothing to do and nothing worth telling anyone.
      }
      source.disconnect();
    }
    for (const gain of this.gains.values()) gain.disconnect();
    this.sources.clear();
    this.gains.clear();
  }

  private audio(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext();
    return this.ctx;
  }
}
